import { useMemo, useState, type CSSProperties } from "react";
import { Icon } from "../lib/Icon";
import { BookSlot } from "../lib/components";
import { StickerPhotoModal } from "../lib/StickerPhotoModal";
import { useCatalog } from "../contexts/CatalogContext";
import { useInventory } from "../contexts/InventoryContext";
import { usePhotos } from "../contexts/PhotosContext";
import { useToast } from "../lib/toast";
import type { Route } from "../App";

function teamSectionId(teamId: number): string {
  return `team-section-${teamId}`;
}

function scrollToTeam(teamId: number): void {
  const el = document.getElementById(teamSectionId(teamId));
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - 140;
  window.scrollTo({ top: y, behavior: "smooth" });
}

type StickerVariant = "default" | "flat" | "photo";
type Filter = "all" | "owned" | "duplicates" | "missing";

interface Props {
  filter?: Filter;
  go?: (r: Route) => void;
  /** Mantenido por compatibilidad — la lista ahora siempre usa slots tipo libro. */
  stickerVariant?: StickerVariant;
}

export function AlbumScreen({ filter = "all", go }: Props) {
  const { teams, stickers, loading: catLoading } = useCatalog();
  const { ownership, cycleCount, promptCount, setCount, loading: invLoading } = useInventory();
  const { primaries, refresh: refreshPhotos } = usePhotos();
  const toast = useToast();

  const [activeFilter, setActiveFilter] = useState<Filter>(filter);
  const [search, setSearch] = useState("");
  const [photoStickerId, setPhotoStickerId] = useState<number | null>(null);

  // Mismo orden que la vista libro: INTRO → grupos A..L → SPECIAL → COCACOLA
  const orderedTeams = useMemo(() => {
    if (teams.length === 0) return [];
    const groupOrder = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];
    const intro = teams.find((t) => t.code === "INTRO");
    const special = teams.find((t) => t.code === "SPECIAL");
    const coke = teams.find((t) => t.code === "COCACOLA");
    const ordered = [];
    if (intro) ordered.push(intro);
    for (const g of groupOrder) {
      ordered.push(...teams.filter((t) => t.group === g));
    }
    if (special) ordered.push(special);
    if (coke) ordered.push(coke);
    // Equipos sin grupo y que no son special/intro/coke (defensivo)
    const seen = new Set(ordered.map((t) => t.id));
    for (const t of teams) if (!seen.has(t.id)) ordered.push(t);
    return ordered;
  }, [teams]);

  const stats = useMemo(() => {
    let owned = 0, dups = 0, missing = 0;
    for (const s of stickers) {
      const c = ownership[s.id] || 0;
      if (c === 0) missing++;
      else if (c >= 2) { owned++; dups += c - 1; }
      else owned++;
    }
    const total = stickers.length;
    const pct = total ? Math.round((owned / total) * 100) : 0;
    return { owned, dups, missing, total, pct };
  }, [stickers, ownership]);

  const filterPredicate = (sId: number, sName: string, sNum: string, teamCode: string, teamName: string): boolean => {
    const c = ownership[sId] || 0;
    if (activeFilter === "missing" && c !== 0) return false;
    if (activeFilter === "duplicates" && c < 2) return false;
    if (activeFilter === "owned" && c < 1) return false;
    if (search) {
      const q = search.toLowerCase().trim();
      const haystack = `${sName} ${sNum} ${teamCode} ${teamName}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  };

  if (catLoading || invLoading) {
    return <div style={{ padding: 40, textAlign: "center", color: "var(--fg-muted)" }}>Cargando catálogo y tu inventario…</div>;
  }

  const handleClick = async (stickerId: number, name: string) => {
    try {
      const cur = ownership[stickerId] || 0;
      // Mismo cycle del context: 0 → 1 → 2 → ... → 5 → 0
      const newVal = cur >= 5 ? 0 : cur + 1;
      await cycleCount(stickerId);
      const label = newVal === 0 ? "vacía" : newVal === 1 ? "pegada" : `repetida ×${newVal}`;
      toast(`${name} → ${label}`);
    } catch (e) {
      toast(`Error guardando: ${e}`);
    }
  };

  const handleAdjust = async (stickerId: number, delta: number, name: string) => {
    const cur = ownership[stickerId] || 0;
    const newVal = Math.max(0, Math.min(99, cur + delta));
    if (newVal === cur) return;
    try {
      await setCount(stickerId, newVal);
      const label = newVal === 0 ? "vacía" : newVal === 1 ? "pegada" : `×${newVal}`;
      toast(`${name} → ${label}`);
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 8, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>Mi álbum</h1>
          <p style={{ color: "var(--fg-muted)", margin: "4px 0 0" }}>
            {stats.owned}/{stats.total} pegadas · {stats.dups} repetidas · {stats.missing} faltantes
          </p>
          <p style={{ color: "var(--fg-muted)", margin: "4px 0 0", fontSize: 12 }}>
            <Icon name="sparkle" size={11} /> Tap en lámina vacía para marcarla. Cuando ya tenés una, aparecen botones <b>−</b> y <b>+</b> para sumar/restar copias. Tocá el número para escribir cantidad exacta.
          </p>
        </div>
        <div style={{ flex: 1 }} />
        <div className="search-box" style={{ width: 280 }}>
          <Icon name="search" size={16} />
          <input
            placeholder='Buscar: jugador, "POR", "Portugal", "12"…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {go && (
          <button className="btn btn-secondary" onClick={() => go("album-book")} title="Vista de álbum doble página">
            <Icon name="book" size={14} /> Vista libro
          </button>
        )}
      </div>

      <div style={{ background: "var(--bg-subtle)", height: 8, borderRadius: 4, marginTop: 16, overflow: "hidden" }}>
        <div style={{ width: `${stats.pct}%`, height: "100%", background: "linear-gradient(90deg, var(--primary), var(--accent-amber))" }} />
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        {[
          { k: "all" as const, label: "Todas", count: stats.total },
          { k: "owned" as const, label: "Pegadas", count: stats.owned },
          { k: "duplicates" as const, label: "Repetidas", count: stats.dups },
          { k: "missing" as const, label: "Faltantes", count: stats.missing },
        ].map((t) => (
          <button key={t.k} className={"tab " + (activeFilter === t.k ? "active" : "")} onClick={() => setActiveFilter(t.k)}>
            {t.label} <span className="count">{t.count}</span>
          </button>
        ))}
      </div>

      <div className="team-nav-bar">
        <div className="team-nav-bar-inner">
          {orderedTeams.map((team) => {
            const teamAll = stickers.filter((s) => s.team_id === team.id);
            const owned = teamAll.filter((s) => (ownership[s.id] || 0) >= 1).length;
            const total = teamAll.length;
            const pct = total ? Math.round((owned / total) * 100) : 0;
            return (
              <button
                key={team.id}
                className="team-nav-chip"
                onClick={() => scrollToTeam(team.id)}
                title={`${team.name} · ${owned}/${total} pegadas`}
              >
                <span className="team-nav-bar-dot" style={{ background: team.color }} />
                <span className="team-nav-chip-code">{team.code}</span>
                <span className="team-nav-chip-pct">{pct}%</span>
              </button>
            );
          })}
        </div>
      </div>

      {orderedTeams.map((team) => {
        const teamStickersAll = stickers.filter((s) => s.team_id === team.id).sort((a, b) => a.position - b.position);
        const teamStickers = teamStickersAll.filter((s) => filterPredicate(s.id, s.name, s.num, team.code, team.name));
        if (teamStickers.length === 0) return null;
        const ownedInTeam = teamStickersAll.filter((s) => (ownership[s.id] || 0) >= 1).length;
        const totalInTeam = teamStickersAll.length;
        const pct = totalInTeam ? Math.round((ownedInTeam / totalInTeam) * 100) : 0;

        return (
          <section
            key={team.id}
            id={teamSectionId(team.id)}
            className="album-team-section"
            style={{
              scrollMarginTop: 140,
              background: `linear-gradient(135deg, ${team.color}1f 0%, var(--bg-card) 60%)`,
              "--team-color-current": team.color,
            } as CSSProperties}
          >
            <header className="album-team-header">
              <div className="album-team-header-left">
                <div className="album-team-we-are">WE ARE</div>
                <div className="album-team-name" style={{ color: team.color }}>
                  {team.name.toUpperCase()}
                </div>
                <div className="album-team-federation">
                  <div className="album-team-flag" style={{ background: team.color }} />
                  <span className="tiny muted">
                    {team.code === "SPECIAL" ? "FIFA Special Stickers" :
                     team.code === "COCACOLA" ? "FIFA × Coca-Cola" :
                     team.code === "INTRO" ? "Roll of Honour" :
                     `Federación ${team.name}`}
                    {team.group && ` · Grupo ${team.group}`}
                  </span>
                </div>
              </div>
              <div className="album-team-header-right">
                <div className="album-team-progress-pct" style={{ color: team.color }}>
                  {pct}%
                </div>
                <div className="tiny muted">{ownedInTeam}/{totalInTeam}</div>
              </div>
            </header>
            <div className="album-team-progress-bar">
              <div className="album-team-progress-fill" style={{ width: `${pct}%`, background: team.color }} />
            </div>
            <div className="sticker-grid">
              {teamStickers.map((s) => (
                <BookSlot
                  key={s.id}
                  sticker={s}
                  teamColor={team.color}
                  count={ownership[s.id] || 0}
                  onClick={() => handleClick(s.id, s.name)}
                  onLongPress={() => promptCount(s.id)}
                  onAdjust={(delta) => handleAdjust(s.id, delta, s.name)}
                  onPhotoClick={() => setPhotoStickerId(s.id)}
                  primaryPhotoUrl={primaries[s.id]}
                />
              ))}
            </div>
          </section>
        );
      })}

      {photoStickerId !== null && (() => {
        const s = stickers.find((x) => x.id === photoStickerId);
        if (!s) return null;
        return (
          <StickerPhotoModal
            stickerId={s.id}
            stickerNum={s.num}
            stickerName={s.name}
            catalogImageUrl={s.image_url}
            onClose={() => setPhotoStickerId(null)}
            onChanged={() => refreshPhotos()}
          />
        );
      })()}
    </div>
  );
}
